import {DataType, DataTypeOperator, FilterRule} from "crqlar-react-common/dist/models/audience/filter.model";
import type {FilterOption} from "crqlar-react-common/dist/models/audience/filterOption.model";
import type {SortDTO} from "crqlar-react-common/dist/models/common/limit";
import type {TFunction} from "i18next";
import {DIGITAL_SIGNING_STATUS_LABEL_KEYS, DIGITAL_SIGNING_STATUSES} from "../../common/uiElements/DigitalSigningStatusElement";
import {DigitalSigningListRequest} from "../digitalSigning.model";

const STATUS_FIELD = 'status';
const SECTOR_FIELD = 'sectorId';
const ISSUED_FIELD = 'issuedAt';

const DAY = /^\d{4}-\d{2}-\d{2}$/;

export const digitalSigningFilterOptions = (t: TFunction): FilterOption[] => [
    {
        field: STATUS_FIELD,
        name: t('Status'),
        localizations: {},
        description: '',
        allowedOperators: [DataTypeOperator.IN],
        predefinedValues: DIGITAL_SIGNING_STATUSES.map(status => ({
            cid: status,
            name: t(DIGITAL_SIGNING_STATUS_LABEL_KEYS[status]),
            colorCode: '',
        })),
        enumValues: DIGITAL_SIGNING_STATUSES,
        dataType: DataType.ENUM,
    },
    {
        field: SECTOR_FIELD,
        name: t('Sector'),
        localizations: {},
        description: '',
        allowedOperators: [DataTypeOperator.IN],
        predefinedValues: [],
        dataType: DataType.TEXT,
        idType: 'sector',
    },
    {
        field: ISSUED_FIELD,
        name: t('Issued'),
        localizations: {},
        description: '',
        allowedOperators: [DataTypeOperator.BETWEEN, DataTypeOperator.MORE_THAN_EQUAL, DataTypeOperator.LESS_THAN_EQUAL],
        predefinedValues: [],
        dataType: DataType.LOCAL_DATE,
    },
];

const allowedValues = <T extends string>(rules: FilterRule[], field: string, known?: readonly T[]): T[] | undefined => {
    const lists = rules
        .filter(rule => rule.field === field)
        .map(rule => (rule.textValues ?? []).filter((value): value is T => !!value && (!known || known.includes(value as T))))
        .filter(values => values.length);

    return lists.length ? lists.reduce((allowed, values) => allowed.filter(value => values.includes(value))) : undefined;
};

const issuedDays = (rules: FilterRule[]) => {
    const firstDays: string[] = [];
    const lastDays: string[] = [];
    const add = (days: string[], day?: string) => {
        if (day && DAY.test(day)) days.push(day);
    };

    rules.filter(rule => rule.field === ISSUED_FIELD).forEach(({operator, localDateValues: [first, second] = []}) => {
        if (operator === DataTypeOperator.BETWEEN) {
            add(firstDays, first);
            add(lastDays, second);
        } else if (operator === DataTypeOperator.MORE_THAN_EQUAL) {
            add(firstDays, first);
        } else if (operator === DataTypeOperator.LESS_THAN_EQUAL) {
            add(lastDays, first);
        }
    });

    return {first: firstDays.sort().pop(), last: lastDays.sort()[0]};
};

const dayBound = (day: string, end: boolean) => {
    const [year, month, date] = day.split('-').map(Number);
    return end ? new Date(year, month - 1, date, 23, 59, 59, 999) : new Date(year, month - 1, date, 0, 0, 0, 0);
};

export const listRequest = (
    rules: FilterRule[],
    search: string,
    page: {pageIndex: number, pageSize: number},
    sorting?: SortDTO
): DigitalSigningListRequest => {
    const issued = issuedDays(rules);

    return {
        statuses: allowedValues(rules, STATUS_FIELD, DIGITAL_SIGNING_STATUSES),
        sectorIds: allowedValues(rules, SECTOR_FIELD),
        from: issued.first ? dayBound(issued.first, false).toISOString() : undefined,
        to: issued.last ? dayBound(issued.last, true).toISOString() : undefined,
        search: search.trim() || undefined,
        limit: {offset: page.pageIndex * page.pageSize, limit: page.pageSize, sort: sorting ? [sorting] : undefined},
    };
};
